import { useEffect, useState, useSyncExternalStore } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Tag, Space, Button, Drawer } from 'antd';
import { useIsMobile } from '../hooks/useMobile';
// Menu 仍用于侧边栏 inline 菜单
import {
  DashboardOutlined,
  TeamOutlined,
  FormOutlined,
  AppstoreAddOutlined,
  UserSwitchOutlined,
  ProfileOutlined,
  FileTextOutlined,
  AuditOutlined,
  WalletOutlined,
  LogoutOutlined,
  CheckSquareOutlined,
  CrownOutlined,
  ExperimentOutlined,
  BookOutlined,
  ApartmentOutlined,
  BulbOutlined,
  CalendarOutlined,
  LineChartOutlined,
  SolutionOutlined,
  FundProjectionScreenOutlined,
  TableOutlined,
  MenuOutlined,
  RiseOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { batchLabel, type Batch } from '../hooks/useBatches';
import { useTenantBrand } from '../hooks/useTenantBrand';
import { EvalRoleLabelsProvider } from '../contexts/EvalRoleLabelsContext';
import { TalentPoolLabelsProvider } from '../contexts/TalentPoolLabelsContext';
import { canSeeMenu } from '../config/access';

const { Header, Sider, Content } = AntLayout;

function subscribeDrawerWidth(cb: () => void) {
  window.addEventListener('resize', cb);
  return () => window.removeEventListener('resize', cb);
}

function getDrawerWidth() {
  return Math.min(300, Math.round(window.innerWidth * 0.88));
}

export function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useAuthStore((s) => s.profile);
  const refreshProfile = useAuthStore((s) => s.refreshProfile);
  const clear = useAuthStore((s) => s.clear);
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const drawerWidth = useSyncExternalStore(subscribeDrawerWidth, getDrawerWidth, () => 300);

  /** 切到 PC 模式时关掉抽屉，避免视觉残留 */
  useEffect(() => {
    if (!isMobile) setMobileMenuOpen(false);
  }, [isMobile]);

  /**
   * Layout 挂载时（每次刷新页面/进入应用都会触发）拉一次 /auth/me 同步最新 profile。
   *
   * 2026-05-12 秦艳芳事故：HR 在「表彰提名指定」给秦艳芳新加了表彰名额后，她本地
   * profile.roles 里仍然缺少 RECOGNITION_NOMINATOR（zustand persist 仅在 setAuth 时
   * 写入 localStorage），导致侧边栏一直不显示「表彰提名」菜单。修复后只要刷新页面
   * 就自动同步角色——不再需要"退出登录再登入"。
   *
   * 放在 Layout 而不是 App 顶层：Layout 只在 RequireAuth 通过后渲染，避免影响
   * LoginPage / DingtalkCallback 的纯登录流程。
   */
  useEffect(() => {
    let cancelled = false;
    api
      .get('/auth/me')
      .then((fresh: any) => {
        if (cancelled) return;
        if (fresh && Array.isArray(fresh.roles)) {
          refreshProfile(fresh);
        }
      })
      .catch(() => {
        /* 401 已由 api 拦截器处理；其他错忽略，保留 localStorage 兜底 */
      });
    return () => {
      cancelled = true;
    };
    // 仅挂载时拉一次，profile 变化不再触发，避免循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roles = profile?.roles ?? [];
  const isAdmin = canSeeMenu(roles, 'ADMIN_CONSOLE');
  const isLeader = canSeeMenu(roles, 'LEADER_EVAL');
  const isNominator = canSeeMenu(roles, 'NOMINATE');
  const isRecognitionNominator = canSeeMenu(roles, 'RECOGNIZE');

  // 顶部 Tag 显示当前批次（业务展示名优先），登录后必拉一次。
  // 选当前 ONGOING/CALCULATING 的非 sandbox 批次；都没有则取最新一条。
  const { data: batches = [] } = useQuery<Batch[]>({
    queryKey: ['batches-header'],
    queryFn: () => api.get('/batches'),
    enabled: !!profile,
    staleTime: 60_000,
  });
  const headerBatch =
    batches.find((b) => b.phase === 'ONGOING' && (b as any).sandbox !== true) ??
    batches.find((b) => b.phase === 'CALCULATING') ??
    batches[0];
  const headerBatchLabel = headerBatch ? `${batchLabel(headerBatch)} 盘点` : '盘点';

  const items: any[] = [];

  // 评价人：统一「我的评价」+ 分模块原入口
  if (!isAdmin) {
    items.push({
      key: '/my/reviews',
      icon: <FundProjectionScreenOutlined />,
      label: '我的评价',
    });
    items.push({ key: '/my/eval-tasks', icon: <FileTextOutlined />, label: '素质项评价' });
  }

  if (isLeader) {
    // V1.2：高潜识别已合并到岗位胜任力评价表单尾部，无单独入口
    items.push(
      { key: '/leader/competency', icon: <ExperimentOutlined />, label: '岗位胜任力+高潜识别' },
    );
  }
  if (isNominator) {
    items.push(
      { key: '/nominator', icon: <ProfileOutlined />, label: '晋升提名' },
    );
  }
  if (isRecognitionNominator) {
    items.push(
      { key: '/recognition-nominator', icon: <CrownOutlined />, label: '表彰提名' },
    );
  }

  if (isAdmin) {
    items.push(
      { type: 'divider' },
      { key: 'admin-group', type: 'group', label: '管理员' },
      { key: '/overview', icon: <DashboardOutlined />, label: '总览看板' },
      { key: '/store-progress', icon: <ShopOutlined />, label: '门店完成度' },
      { key: '/batches', icon: <CalendarOutlined />, label: '盘点批次' },
      { key: '/employees', icon: <TeamOutlined />, label: '花名册' },
      {
        key: 'quality-group',
        icon: <BulbOutlined />,
        label: '素质项评价',
        children: [
          { key: '/templates/quality', icon: <BookOutlined />, label: '维度库' },
          { key: '/eval-relations', icon: <ApartmentOutlined />, label: '评价关系' },
          { key: '/eval-dispatch', icon: <FormOutlined />, label: '素质项派单' },
          { key: '/eval-tasks', icon: <CheckSquareOutlined />, label: '评价进度' },
          { key: '/eval-results', icon: <TableOutlined />, label: '评价结果一览' },
          { key: '/data-dictionary', icon: <ProfileOutlined />, label: '数据字典' },
        ],
      },
      {
        key: 'performance-group',
        icon: <LineChartOutlined />,
        label: '业绩维度',
        children: [
          { key: '/performance/monthly', icon: <LineChartOutlined />, label: '绩效业绩' },
          {
            key: '/performance/port-cutoffs',
            icon: <LineChartOutlined />,
            label: '业绩端口 · 切档配置',
          },
        ],
      },
      {
        key: 'comp-hp-group',
        icon: <ExperimentOutlined />,
        label: '岗位胜任力 + 高潜',
        children: [
          { key: '/competency/models', icon: <BookOutlined />, label: '胜任力模型库' },
          { key: '/competency/dispatch', icon: <FormOutlined />, label: '胜任力派单' },
          { key: '/hp', icon: <CrownOutlined />, label: '高潜题库与阈值' },
          {
            key: '/competency-hp/results',
            icon: <CheckSquareOutlined />,
            label: '评价汇总（合并）',
          },
        ],
      },
      { key: '/ninebox', icon: <AppstoreAddOutlined />, label: '九宫格' },
      {
        key: 'nomination-group',
        icon: <SolutionOutlined />,
        label: '职级晋升和表彰提名',
        children: [
          { key: '/nomination-leaders', icon: <UserSwitchOutlined />, label: '提名领导指定' },
          { key: '/nominations', icon: <ProfileOutlined />, label: '提名汇总' },
          { key: '/recognition-leaders', icon: <CrownOutlined />, label: '表彰提名指定' },
        ],
      },
      { key: '/promotion-plan', icon: <RiseOutlined />, label: '职级晋升和表彰人员' },
      { key: '/reports', icon: <FileTextOutlined />, label: '报表中心' },
      { key: '/billing', icon: <WalletOutlined />, label: '账户与充值' },
      { key: '/audit', icon: <AuditOutlined />, label: '操作审计' },
    );
  }

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: () => { clear(); navigate('/login'); },
    },
  ];

  const menuSelectedKey = location.pathname.startsWith('/batches')
    ? '/batches'
    : location.pathname;

  const onNavMenuClick = ({ key }: { key: string }) => {
    if (typeof key === 'string' && key.startsWith('/')) {
      navigate(key);
      setMobileMenuOpen(false);
    }
  };

  const { tenantName } = useTenantBrand();
  const brandText = tenantName ? `${tenantName}人才盘点` : '人才盘点';
  const brand = (
    <div style={{ padding: '16px 20px', fontSize: 15, fontWeight: 700, color: '#7c5cbf', lineHeight: 1.4 }}>
      {brandText}
    </div>
  );

  const sideMenu = (
    <Menu
      mode="inline"
      selectedKeys={[menuSelectedKey]}
      defaultOpenKeys={['quality-group', 'performance-group', 'comp-hp-group', 'nomination-group']}
      onClick={onNavMenuClick}
      items={items}
      style={isMobile ? { borderInlineEnd: 'none', background: 'transparent' } : undefined}
    />
  );

  const headerPadding = isMobile ? undefined : '0 24px';
  const contentPadding = 24;

  return (
    <AntLayout
      className={isMobile ? undefined : 'tg-app-layout'}
      style={isMobile ? { minHeight: '100vh' } : undefined}
    >
      {!isMobile && (
        <Sider
          width={220}
          theme="light"
          className="tg-app-sider"
          style={{ borderRight: '1px solid #eee' }}
        >
          <div className="tg-app-sider-brand">{brand}</div>
          <div className="tg-app-sider-menu">{sideMenu}</div>
        </Sider>
      )}
      <AntLayout className={isMobile ? undefined : 'tg-app-main'}>
        <Header
          className={isMobile ? 'tg-mobile-header' : undefined}
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            background: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: headerPadding ?? '0 12px',
            borderBottom: isMobile ? undefined : '1px solid #eee',
            boxShadow: '0 2px 10px rgba(15,23,42,0.06)',
            gap: 8,
            flexWrap: 'nowrap',
            height: 'auto',
            minHeight: 64,
          }}
        >
          <Space wrap={false} size={isMobile ? 6 : 12} style={{ flex: 1, minWidth: 0 }}>
            {isMobile && (
              <Button
                type="primary"
                ghost
                icon={<MenuOutlined />}
                aria-label="打开导航"
                onClick={() => setMobileMenuOpen(true)}
                style={{
                  borderRadius: 12,
                  width: 44,
                  height: 44,
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            )}
            <Tag
              color="purple"
              style={
                isMobile
                  ? {
                      marginInlineEnd: 0,
                      borderRadius: 999,
                      padding: '4px 12px',
                      fontWeight: 600,
                      border: 'none',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }
                  : undefined
              }
            >
              {headerBatchLabel}
            </Tag>
          </Space>
          <Dropdown menu={{ items: userMenuItems }} trigger={['click']}>
            <Space style={{ cursor: 'pointer', flexShrink: 0 }} size={isMobile ? 6 : 12}>
              <Avatar
                size={isMobile ? 'default' : 'default'}
                src={profile?.avatar}
                style={isMobile ? { border: '2px solid rgba(155,125,216,0.35)' } : undefined}
              >
                {profile?.name?.[0]}
              </Avatar>
              {isMobile && profile?.name && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    lineHeight: 1.2,
                  }}
                >
                  <span
                    style={{
                      maxWidth: 88,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: 600,
                      fontSize: 13,
                      color: '#334155',
                    }}
                  >
                    {profile.name}
                  </span>
                  <span
                    style={{
                      fontSize: 9,
                      color: '#cbd5e1',
                      marginTop: 1,
                      letterSpacing: 0.2,
                    }}
                  >
                    v {__TG_BUILD_AT__}
                  </span>
                </div>
              )}
              {!isMobile && (
                <div style={{ lineHeight: '20px' }}>
                  <div style={{ fontWeight: 500 }}>{profile?.name}</div>
                  <div style={{ fontSize: 11, color: '#999' }}>
                    {profile?.roles.join(' / ')}
                  </div>
                  <div style={{ fontSize: 11, color: '#ccc', marginTop: 2 }}>
                    v {__TG_BUILD_AT__}
                  </div>
                </div>
              )}
            </Space>
          </Dropdown>
        </Header>
        <Drawer
          title={
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#5b3fa8', letterSpacing: 0.3 }}>
                {brandText}
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400, marginTop: 4 }}>
                选择要进入的模块
              </div>
            </div>
          }
          placement="left"
          width={drawerWidth}
          closable
          destroyOnClose={false}
          open={isMobile && mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          styles={{
            body: {
              padding: 0,
              background: 'linear-gradient(180deg, #faf8ff 0%, #ffffff 22%)',
            },
            header: { borderBottom: '1px solid rgba(124, 92, 191, 0.12)' },
            content: {
              borderTopRightRadius: 16,
              borderBottomRightRadius: 16,
            },
          }}
        >
          <div style={{ padding: '4px 8px 12px' }}>{sideMenu}</div>
        </Drawer>
        <Content
          className={isMobile ? 'tg-mobile-content' : 'tg-app-content'}
          style={isMobile ? { minWidth: 0 } : { padding: contentPadding, minWidth: 0 }}
        >
          <EvalRoleLabelsProvider>
            <TalentPoolLabelsProvider>
              <Outlet />
            </TalentPoolLabelsProvider>
          </EvalRoleLabelsProvider>
        </Content>
      </AntLayout>
    </AntLayout>
  );
}
